import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredArticles = currentCategory
    ? articles.filter((a) => a.categorySlug === slug)
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="container-main py-20 text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类不存在</h2>
            <p className="text-foreground-600 mb-6">您访问的分类页面不存在或已被移除。</p>
            <button
              onClick={() => navigate('/')}
              className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </>
    );
  }

  const collectionPageLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 欧美一区二区`,
    description: currentCategory.description,
    url: `https://oumei-yiqierqu.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://oumei-yiqierqu.com/article/${a.id}`,
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionPageLd)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100">
          <div className="container-main py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="flex items-center gap-3 mb-2">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-lg`}></i>
              </span>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {currentCategory.name}
              </h1>
            </div>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl ml-13">
              {currentCategory.description}
            </p>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="container-main py-3 flex items-center gap-2 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  navigate(`/category/${cat.slug}`);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="container-main py-10 md:py-14">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600">该分区暂无文章，敬请期待</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}